import store from "@/store";
import { HOST } from "@/api/base";

const CHUNK_SIZE = 2 * 1024 * 1024;

function storageKey(packageType, fileKey) {
  return `client_pkg_upload_${packageType}_${fileKey}`;
}

function readSession(packageType, fileKey) {
  try {
    const raw = localStorage.getItem(storageKey(packageType, fileKey));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function writeSession(packageType, fileKey, data) {
  localStorage.setItem(storageKey(packageType, fileKey), JSON.stringify(data));
}

function clearSession(packageType, fileKey) {
  localStorage.removeItem(storageKey(packageType, fileKey));
}

function fileKeyOf(file) {
  return `${file.name}_${file.size}_${file.lastModified || 0}`;
}

function uploadChunkXHR(url, formData, token, onChunkProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    if (token) {
      xhr.setRequestHeader("token", token);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onChunkProgress) {
        onChunkProgress(e.loaded, e.total);
      }
    };
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText || "{}");
        if (xhr.status >= 200 && xhr.status < 300 && data.status === "success") {
          resolve(data);
        } else {
          reject(new Error((data && data.msg) || "分片上传失败"));
        }
      } catch (err) {
        reject(err);
      }
    };
    xhr.onerror = () => reject(new Error("网络错误"));
    xhr.send(formData);
  });
}

async function fetchChunkStatus(packageType, uploadId, packageChannel) {
  const token = store.state.loginAccountInfo?.token;
  const res = await fetch(`${HOST}/${packageType}Version/chunk/status`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      token: token || "",
    },
    body: JSON.stringify({
      upload_id: uploadId,
      package_channel: packageChannel || "",
    }),
  });
  const data = await res.json();
  if (data.status !== "success") {
    throw new Error(data.msg || "查询分片状态失败");
  }
  return data.data || {};
}

async function abortUpload(packageType, uploadId, packageChannel) {
  if (!uploadId) return;
  const token = store.state.loginAccountInfo?.token;
  await fetch(`${HOST}/${packageType}Version/chunk/abort`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      token: token || "",
    },
    body: JSON.stringify({
      upload_id: uploadId,
      package_channel: packageChannel || "",
    }),
  });
}

async function completeUpload(packageType, paramKey, uploadId, formData, totalChunks) {
  const token = store.state.loginAccountInfo?.token;
  const body = new FormData();
  body.append("upload_id", uploadId);
  body.append("total_chunks", String(totalChunks));
  body.append(paramKey, JSON.stringify(formData));
  const res = await fetch(`${HOST}/${packageType}Version/chunk/complete`, {
    method: "POST",
    headers: { token: token || "" },
    body,
  });
  const data = await res.json();
  if (data.status !== "success") {
    throw new Error(data.msg || "合并上传失败");
  }
  return data;
}

/**
 * 分片上传（支持断点续传）
 */
export async function uploadClientPackage({
  packageType,
  packageChannel,
  file,
  paramKey,
  formData,
  onProgress,
  uploadId: forcedUploadId,
}) {
  const channel =
    packageChannel ||
    (formData && formData.channel) ||
    "";
  const fileKey = fileKeyOf(file);
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  const saved = readSession(packageType, fileKey) || {};
  const uploadId =
    forcedUploadId || saved.uploadId || `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  let uploadedSet = new Set(saved.uploadedChunks || []);
  try {
    const status = await fetchChunkStatus(packageType, uploadId, channel);
    const remote = status.uploaded_chunks || [];
    remote.forEach((i) => uploadedSet.add(i));
  } catch (e) {
    // 新会话
  }

  const token = store.state.loginAccountInfo?.token;
  const baseUrl = `${HOST}/${packageType}Version/chunk/upload`;
  let uploadedBytes = 0;
  uploadedSet.forEach((idx) => {
    const start = idx * CHUNK_SIZE;
    const end = Math.min(file.size, start + CHUNK_SIZE);
    uploadedBytes += end - start;
  });
  if (onProgress) onProgress(Math.min(99, Math.floor((uploadedBytes / file.size) * 100)));

  for (let i = 0; i < totalChunks; i++) {
    if (uploadedSet.has(i)) {
      continue;
    }
    const start = i * CHUNK_SIZE;
    const end = Math.min(file.size, start + CHUNK_SIZE);
    const blob = file.slice(start, end);
    const fd = new FormData();
    fd.append("file", blob, file.name);
    fd.append("upload_id", uploadId);
    fd.append("chunk_index", String(i));
    fd.append("total_chunks", String(totalChunks));
    fd.append("file_key", fileKey);
    fd.append("file_name", file.name);
    fd.append("file_size", String(file.size));
    if (channel) {
      fd.append("package_channel", channel);
    }

    await uploadChunkXHR(baseUrl, fd, token, (loaded) => {
      const current =
        uploadedBytes + loaded - (uploadedSet.has(i) ? 0 : 0);
      const done = Math.min(file.size, start + loaded);
      let sum = 0;
      uploadedSet.forEach((idx) => {
        const s = idx * CHUNK_SIZE;
        const e = Math.min(file.size, s + CHUNK_SIZE);
        sum += e - s;
      });
      sum += done - start;
      if (onProgress) {
        onProgress(Math.min(99, Math.floor((sum / file.size) * 100)));
      }
    });

    uploadedSet.add(i);
    uploadedBytes += end - start;
    writeSession(packageType, fileKey, {
      uploadId,
      uploadedChunks: Array.from(uploadedSet).sort((a, b) => a - b),
      fileKey,
      fileName: file.name,
      fileSize: file.size,
    });
    if (onProgress) {
      onProgress(Math.min(99, Math.floor((uploadedBytes / file.size) * 100)));
    }
  }

  const result = await completeUpload(packageType, paramKey, uploadId, formData, totalChunks);
  clearSession(packageType, fileKey);
  if (onProgress) onProgress(100);
  return result;
}

export async function removeClientPackageUpload(
  packageType,
  file,
  uploadId,
  packageChannel
) {
  const fileKey = file ? fileKeyOf(file) : null;
  const saved = fileKey ? readSession(packageType, fileKey) : null;
  const id = uploadId || (saved && saved.uploadId);
  if (id) {
    await abortUpload(packageType, id, packageChannel);
  }
  if (fileKey) {
    clearSession(packageType, fileKey);
  }
}

export function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB";
}
